import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const Arrow = () => (
    <svg className="lp-btn-arrow" viewBox="0 0 14 10" fill="none" aria-hidden="true">
        <path d="M1 5H13M13 5L9 1M13 5L9 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/**
 * Primary CTA with rotating conic-gradient border glow (via CSS)
 * and magnetic hover on desktop pointer devices.
 */
export default function PrimaryCTA({ children, onClick, type = "button", disabled = false, showArrow = true, dataTestId, ...rest }) {
    const ref = useRef(null);
    const rawX = useMotionValue(0);
    const rawY = useMotionValue(0);
    const x = useSpring(rawX, { stiffness: 240, damping: 22, mass: 0.4 });
    const y = useSpring(rawY, { stiffness: 240, damping: 22, mass: 0.4 });
    const isCoarse = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

    const onMouseMove = (e) => {
        if (isCoarse || !ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.hypot(dx, dy);
        if (dist < 90) {
            const strength = 1 - dist / 90;
            rawX.set(dx * strength * 0.18);
            rawY.set(dy * strength * 0.18);
        }
    };
    const onMouseLeave = () => { rawX.set(0); rawY.set(0); };

    return (
        <motion.button
            ref={ref}
            type={type}
            className="lp-btn-primary"
            onClick={onClick}
            disabled={disabled}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            style={{ x, y }}
            data-testid={dataTestId}
            {...rest}
        >
            <span style={{ position: "relative", zIndex: 2, display: "inline-flex", alignItems: "center", gap: 10 }}>
                {children}
                {showArrow && <Arrow />}
            </span>
        </motion.button>
    );
}
